import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Upload, BusFront, IdCard, Loader2, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const toDMY = (d) => `${String(d.getUTCDate()).padStart(2, "0")}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
// Normalise any supported date value to dd-MMM-yyyy (e.g. 15-Jan-2027).
const fmtDate = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  // Excel serial number (date cells come through as a number)
  if (/^\d+(\.\d+)?$/.test(s)) {
    const d = new Date(Math.round((parseFloat(s) - 25569) * 86400000));
    return isNaN(d) ? s : toDMY(d);
  }
  // dd-MMM-yyyy / dd MMM yy (named month)
  let m = s.match(/^(\d{1,2})[-/\s]([A-Za-z]{3,})[-/\s](\d{2,4})$/);
  if (m) {
    const mon = MONTHS.findIndex((x) => x.toLowerCase() === m[2].slice(0, 3).toLowerCase());
    const yr = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    if (mon >= 0) return toDMY(new Date(Date.UTC(yr, mon, Number(m[1]))));
  }
  // yyyy-MM-dd
  m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) return toDMY(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
  // dd-MM-yyyy / dd/MM/yyyy (numeric month)
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) return toDMY(new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]))));
  const d = new Date(s);
  return isNaN(d) ? s : toDMY(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())));
};

const pick = (row, aliases) => {
  const keys = Object.keys(row);
  for (const a of aliases) {
    const k = keys.find((x) => norm(x) === a || norm(x).includes(a));
    if (k && String(row[k]).trim() !== "") return String(row[k]).trim();
  }
  return "";
};

// Parse pasted text (comma or tab separated) into array of objects keyed by header.
function parseText(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return [];
  const delim = lines[0].includes("\t") ? "\t" : ",";
  const split = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (c === delim && !q) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out.map((x) => x.trim());
  };
  const headers = split(lines[0]);
  return lines.slice(1).map((l) => {
    const cells = split(l); const o = {};
    headers.forEach((h, i) => { o[h] = cells[i] ?? ""; });
    return o;
  });
}

async function parseFile(file) {
  const buf = new Uint8Array(await file.arrayBuffer());
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: "" });
}

function ImportPanel({ kind, mapRow, columns, extraControls, buildBody, endpoint, icon: Icon, title, hint }) {
  const [text, setText] = useState("");
  const [raw, setRaw] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const rows = useMemo(() => raw.map(mapRow).filter(Boolean), [raw, mapRow]);

  const onText = (v) => { setText(v); setResult(null); try { setRaw(parseText(v)); } catch { setRaw([]); } };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setResult(null);
    try { const r = await parseFile(f); setRaw(r); setText(`${f.name} · ${r.length} row(s) loaded`); }
    catch (err) { toast.error("Could not read that file - export as .csv or .xlsx"); }
  };

  const doImport = async () => {
    if (!rows.length) return toast.error("Nothing to import - paste rows or upload a file first");
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, buildBody(rows));
      setResult(data);
      toast.success(`${data.created} ${kind} imported · ${data.skipped_count} skipped`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-soft space-y-4 p-5" data-testid={`import-${kind}-panel`}>
      <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[#0052FF]" /><h3 className="font-display text-lg font-bold">{title}</h3></div>
      <p className="text-sm text-slate-500">{hint}</p>
      {extraControls}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50" data-testid={`import-${kind}-file`}>
          <FileSpreadsheet className="h-4 w-4" />Upload .csv / .xlsx
          <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={onFile} />
        </label>
        <span className="text-xs text-slate-400">or paste rows (with a header row) below</span>
      </div>
      <Textarea value={text} onChange={(e) => onText(e.target.value)} rows={5} placeholder={`Paste here, e.g.\n${columns.map((c) => c.sample[0]).join(",")}\n${columns.map((c) => c.sample[1]).join(",")}`} className="font-mono text-xs" data-testid={`import-${kind}-textarea`} />

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-100" data-testid={`import-${kind}-preview`}>
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>{columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}</tr></thead>
            <tbody>
              {rows.slice(0, 8).map((r, i) => <tr key={i} className="border-t border-slate-100">{columns.map((c) => <td key={c.key} className="px-3 py-1.5">{r[c.key] || <span className="text-slate-300">-</span>}</td>)}</tr>)}
            </tbody>
          </table>
          <div className="px-3 py-2 text-xs text-slate-500">{rows.length} row(s) ready{rows.length > 8 ? " (showing first 8)" : ""}</div>
        </div>
      )}

      <Button disabled={busy || !rows.length} onClick={doImport} className="rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid={`import-${kind}-button`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Import {rows.length || ""} {kind}
      </Button>

      {result && (
        <div className="space-y-2 rounded-xl bg-emerald-50 p-4 text-sm" data-testid={`import-${kind}-result`}>
          <div className="flex items-center gap-2 font-bold text-emerald-800"><CheckCircle2 className="h-5 w-5" />{result.created} {kind} imported{result.bus_types_created ? ` · ${result.bus_types_created} new bus type(s)` : ""}{result.owner ? ` · owner: ${result.owner.name}${result.owner.created ? " (created)" : ""}` : ""}</div>
          {result.home_station && <div className="text-emerald-700">Home station: {result.home_station}</div>}
          {result.skipped_count > 0 && (
            <details className="text-slate-600"><summary className="cursor-pointer font-semibold">{result.skipped_count} skipped (click to see why)</summary>
              <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">{result.skipped.map((s, i) => <li key={i} className="text-xs">· <b>{s.number || s.name}</b> — {s.reason}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function DataImport() {
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [ownerName, setOwnerName] = useState("GPRTU");
  const [ownerPhone, setOwnerPhone] = useState("");

  useEffect(() => {
    api.get("/admin/stations").then(({ data }) => {
      setStations(data);
      const neo = data.find((s) => /neoplan/i.test(s.name) || /neoplan/i.test(s.city)) || data.find((s) => /accra/i.test(s.city)) || data[0];
      if (neo) setStationId(neo.id);
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="data-import-page">
      <div>
        <h2 className="font-display text-2xl font-bold">Import data</h2>
        <p className="text-sm text-slate-500">Bring in Bus and Driver records from a spreadsheet. Export your query results to .csv or .xlsx and upload, or paste the rows. Duplicates (same bus number / driver phone) are skipped automatically.</p>
      </div>

      <ImportPanel
        kind="buses" title="Buses" icon={BusFront} endpoint="/admin/import/buses"
        hint="Needs a Bus number column and a Total seats column. The bus type is matched by seat count (a new type is created if none exists); all buses get the home station and owner chosen below."
        columns={[
          { key: "number", label: "Bus number", sample: ["BusNumber", "GR-1234-20"] },
          { key: "seats", label: "Seats", sample: ["TotalSeats", "45"] },
          { key: "name", label: "Name (optional)", sample: ["Name", ""] },
        ]}
        mapRow={(r) => {
          const number = pick(r, ["busnumber", "busno", "registration", "regno", "reg", "vehicleno", "vehicle", "coachno", "number"]);
          const seats = pick(r, ["totalseats", "seats", "seat", "capacity", "noofseats", "noseats"]);
          if (!number) return null;
          return { number, seats: parseInt(seats || "0", 10) || 0, name: pick(r, ["busname", "name"]) };
        }}
        extraControls={
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label className="text-xs font-semibold text-slate-600">Home station (all imported buses)</Label>
              <Select value={stationId} onValueChange={setStationId}>
                <SelectTrigger data-testid="import-home-station"><SelectValue placeholder="Select station" /></SelectTrigger>
                <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs font-semibold text-slate-600">Owner name (all buses)</Label>
              <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} data-testid="import-owner-name" /></div>
            <div className="space-y-1"><Label className="text-xs font-semibold text-slate-600">Owner phone (optional)</Label>
              <Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} placeholder="024 000 0000" data-testid="import-owner-phone" /></div>
          </div>
        }
        buildBody={(rows) => ({ rows, home_station_id: stationId, owner_name: ownerName, owner_phone: ownerPhone, layout: "2+2" })}
      />

      <ImportPanel
        kind="drivers" title="Drivers" icon={IdCard} endpoint="/admin/import/drivers"
        hint="Needs at least a Name. Phone, Licence number and Licence expiry are matched by their usual column names."
        columns={[
          { key: "name", label: "Name", sample: ["Name", "Kwame Mensah"] },
          { key: "phone", label: "Phone", sample: ["Phone", "0241234567"] },
          { key: "license_no", label: "Licence no.", sample: ["LicenseNo", "DL-00123"] },
          { key: "license_expiry", label: "Licence expiry", sample: ["Expiry", "01-May-2027"] },
        ]}
        mapRow={(r) => {
          const name = pick(r, ["drivername", "fullname", "name"]);
          if (!name) return null;
          return { name, phone: pick(r, ["phone", "mobile", "contact", "phoneno", "tel", "msisdn"]).replace(/^'+/, ""),
            license_no: pick(r, ["licenseno", "licenceno", "licensenumber", "licencenumber", "license", "licence", "dlno", "dl"]),
            license_expiry: fmtDate(pick(r, ["licenseexpiry", "licenceexpiry", "expirydate", "expiry", "validtill", "validto", "valid", "dlexpdate", "dlexp"])) };
        }}
        buildBody={(rows) => ({ rows })}
      />
    </div>
  );
}
